import { Component, inject } from '@angular/core';
import { DialogService } from '../../../core/services/dialog.service';
import { UwButton } from '../button/uw-button';
import { FocusTrapDirective } from '../../directives/focus-trap.directive';
import { UwIcon } from '../icon/uw-icon';

@Component({
  selector: 'uw-dialog',
  standalone: true,
  imports: [UwButton, FocusTrapDirective, UwIcon],
  template: `
    @if (dialogService.dialog(); as dialog) {
      <div class="fixed inset-0 z-modal flex items-center justify-center p-4">
        <div
          class="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
          (click)="dialogService.close()"
          [attr.aria-hidden]="'true'"
        ></div>
        <div
          uwFocusTrap
          role="dialog"
          aria-modal="true"
          [attr.aria-labelledby]="'uw-dlg-' + dialog.id"
          class="relative z-10 w-full max-w-md rounded-xl border border-line bg-surface-elevated shadow-lg"
          (keydown.escape)="dialogService.close()"
        >
          <header class="flex items-center gap-2.5 border-b border-line px-5 py-4">
            <span class="flex h-8 w-8 items-center justify-center rounded-lg" [class]="toneClass(dialog.tone)">
              <uw-icon [name]="dialog.tone === 'danger' ? 'alert-triangle' : 'help-circle'" [size]="16" />
            </span>
            <h2 [id]="'uw-dlg-' + dialog.id" class="typo-title text-primary">{{ dialog.title }}</h2>
            <button
              class="ml-auto rounded p-1 text-text-muted transition-colors hover:bg-surface-sunken hover:text-primary"
              (click)="dialogService.close()"
              [attr.aria-label]="'Close dialog'"
            >
              <uw-icon name="x" [size]="16" />
            </button>
          </header>
          <div class="px-5 py-4">
            <p class="text-[13px] leading-relaxed text-secondary">{{ dialog.message }}</p>
            @if (dialog.details?.length) {
              <ul class="mt-3 space-y-1.5 rounded-lg bg-surface-sunken p-3">
                @for (detail of dialog.details; track detail) {
                  <li class="flex items-start gap-2 text-[12px] text-secondary">
                    <uw-icon name="circle-dot" [size]="13" class="mt-0.5 shrink-0 text-ai" />
                    {{ detail }}
                  </li>
                }
              </ul>
            }
          </div>
          <footer class="flex justify-end gap-2 border-t border-line bg-surface-sunken/50 px-5 py-3">
            <uw-button variant="ghost" (click)="dialogService.close()">{{ dialog.cancelLabel }}</uw-button>
            <uw-button [variant]="dialog.tone === 'danger' ? 'danger' : 'primary'" (click)="dialogService.runConfirm()">
              {{ dialog.confirmLabel }}
            </uw-button>
          </footer>
        </div>
      </div>
    }
  `,
})
export class UwDialog {
  protected readonly dialogService = inject(DialogService);

  protected toneClass(tone?: string): string {
    switch (tone) {
      case 'danger':
        return 'bg-danger-soft text-danger';
      case 'warning':
        return 'bg-warning-soft text-warning';
      case 'success':
        return 'bg-success-soft text-success';
      default:
        return 'bg-ai-soft text-ai';
    }
  }
}
